import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { config } from '@/config/env.config';
import { queryClient } from '@/lib/query-client';
import {
  attachAxiosSentryHandlers,
  syncSentryAuthContext,
} from '@/observability/sentry.axios';

// Estender o tipo de config do axios para permitir desabilitar notificações
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipErrorNotification?: boolean;
    skipAuthRedirect?: boolean;
  }
}

const api = axios.create({
  baseURL: config.api.baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token JWT
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    // Não sobrescreve um token explícito (ex.: vínculo do Wave com a conta recém-logada)
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    syncSentryAuthContext();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor para tratar erros
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const statusCode = error.response?.status;
    const requestConfig = error.config as InternalAxiosRequestConfig;
    const requestUrl = requestConfig?.url?.toLowerCase() || '';
    const skipAuthRedirect = requestConfig?.skipAuthRedirect;
    const isLoginRequest = requestUrl.includes('/auth/login');

    // Tratamento especial para 401 - redireciona para login apenas em rotas protegidas
    if (statusCode === 401 && !skipAuthRedirect && !isLoginRequest) {
      // Limpar cache do React Query
      queryClient.clear();

      // Limpar dados de autenticação do localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('auth-storage');
      localStorage.removeItem('user');

      // Redirecionar para login com reset completo
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Para 401 de rotas públicas (ex.: login), deixar o componente tratar a mensagem
    if (statusCode === 401) {
      return Promise.reject(error);
    }

    // Tratamento especial para 429 (Rate Limit) - não mostrar notificação aqui
    // Deixar o componente tratar para mostrar mensagem mais detalhada
    if (error.response?.status === 429) {
      return Promise.reject(error);
    }

    // Se a requisição configurou para pular notificação, não mostra
    const skipNotification = requestConfig?.skipErrorNotification;
    if (skipNotification) {
      return Promise.reject(error);
    }

    // Extrair mensagem de erro do backend
    const data = error.response?.data as any;
    // Rotas OAuth (ex.: /mcp-oauth) respondem { error, error_description } em vez de { message }
    const backendMessage = data?.message || data?.error_description;
    const errorMessage =
      Array.isArray(backendMessage)
        ? backendMessage[0]
        : backendMessage ||
          error.message ||
          'Ocorreu um erro inesperado. Tente novamente.';

    // Mostrar notificação de erro
    // id fixo por mensagem: a mesma falha repetida (ex.: várias requisições) mostra um toast só
    toast.error('Erro', {
      id: `api-error:${errorMessage}`,
      description: errorMessage,
      duration: 5000,
    });

    return Promise.reject(error);
  },
);

attachAxiosSentryHandlers(api);

export default api;
